import { use, useEffect } from 'react';
import { FieldArrayWithId, useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import { DeviceType } from 'shared/models/device-type';

import CreateDeviceCommand from 'features/device/domain/models/create-device-command';
import { useCreateDevice } from 'features/device/domain/queries/device-queries';
import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import IPUnitDoorEntrance from 'features/systems/ip-unit/domain/models/ip-unit-door-entrance';
import IPUnitProviderContext from 'features/systems/ip-unit/providers/contexts/ip-unit-provider-context';

export type IPUnitDoorEntranceField = FieldArrayWithId<{ doorEntrances: IPUnitDoorEntrance[] }, 'doorEntrances', 'keyId'>;

type IPUnitDoorEntrancesHook = {
  doorEntrances: IPUnitDoorEntranceField[];
  addDoorEntrance: () => void;
  deleteDoorEntrance: (index: number) => void;
  createDevice: (doorEntrance: IPUnitDoorEntrance) => void;
};

const useIPUnitDoorEntrances = (): IPUnitDoorEntrancesHook => {
  const { selectedSystem, openDeviceDetailsByDeviceId } = use(IPUnitProviderContext);
  const {
    systemsDetailsHook: { refreshSystem },
  } = useSystemsContextProvider();
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const {
    mutate: createDeviceMutation,
    isSuccess: createDeviceIsSuccess,
    isError: createDeviceIsError,
    error: createDeviceError,
    data: createDeviceData,
  } = useCreateDevice();

  const { control } = useFormContext<{ doorEntrances: IPUnitDoorEntrance[] }>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'doorEntrances',
    keyName: 'keyId',
  });

  const addDoorEntrance = (): void => {
    if (fields.length < 9) {
      append({ id: uuidv4(), number: '' });
    }
  };

  const deleteDoorEntrance = (index: number): void => {
    remove(index);
  };

  const createDevice = (doorEntrance: IPUnitDoorEntrance): void => {
    if (!selectedSystem) {
      return;
    }

    const createDeviceCommand: CreateDeviceCommand = {
      externalId: `${selectedSystem.macAddress}_deurmelding_${doorEntrance.number}`,
      name: `${selectedSystem.macAddress} deurmelding ${doorEntrance.number}`,
      type: DeviceType.DoorAlarm,
      systemId: selectedSystem.id,
      commands: [],
    };

    createDeviceMutation(createDeviceCommand);
  };

  useEffect(() => {
    if (createDeviceIsSuccess && createDeviceData) {
      openDeviceDetailsByDeviceId(createDeviceData?.id ?? '');
      refreshSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createDeviceData]);

  useEffect(() => {
    if (createDeviceIsError && createDeviceError) {
      setErrorMessage(createDeviceError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createDeviceError]);

  return {
    doorEntrances: fields,
    addDoorEntrance,
    deleteDoorEntrance,
    createDevice,
  };
};

export default useIPUnitDoorEntrances;
